import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Minus } from "../../icons/svg/outline";
import { itemMatchesSearchQuery } from "../../../utils/common/searchFilter";

/**
 * SearchableDetailsDropdown is the legacy compact detail/table dropdown.
 *
 * It supports optional multi-select staging and tooltip rows used by dispatch scope
 * details and T&M line items. For normal form inputs, use SearchableDropdown.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.options - Options as {id, label, tooltip?}.
 * @param {boolean} [props.multiSelect=false] - Whether selections are staged as a list.
 * @param {string|number} props.value - Current selected option ID.
 * @param {Function} props.onChange - Change handler.
 * @param {string} [props.placeholder="Select option"] - Empty placeholder.
 * @param {boolean} [props.disabled=false] - Whether the dropdown is disabled.
 * @param {string} [props.className=""] - Wrapper classes.
 * @param {number} [props.dropdownMinWidth] - Minimum dropdown width.
 * @returns {JSX.Element}
 */
const SearchableDetailsDropdown = ({
  options = [],
  multiSelect = false,
  value,
  onChange,
  placeholder = "Select option",
  disabled = false,
  className = "",
  dropdownMinWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [keyboardIndex, setKeyboardIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [hoveredOption, setHoveredOption] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [selectedOptions, setSelectedOptions] = useState(value ? [value] : []);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const optionRefs = useRef({});

  useEffect(() => {
    if (multiSelect && value) {
      setSelectedOptions([value]);
    }
  }, [multiSelect, value]);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) {
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (isOpen && listRef.current && keyboardIndex !== null) {
      listRef.current.children[keyboardIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [keyboardIndex, isOpen]);

  const handleClose = useCallback(
    (id) => {
      if (id) {
        onChange?.(id);
      } else {
        onChange?.(selectedOptions);
      }
      setIsOpen(false);
      setSearchTerm("");
      setKeyboardIndex(null);
      setHoveredOption(null);
    },
    [onChange, selectedOptions]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        listRef.current &&
        !listRef.current.contains(event.target)
      ) {
        if (multiSelect) {
          onChange?.(selectedOptions);
        }
        setIsOpen(false);
        setSearchTerm("");
        setKeyboardIndex(null);
        setHoveredOption(null);
        setSelectedOptions(value ? [value] : []);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, value, multiSelect, selectedOptions, onChange]);

  const handleOptionHover = (option, element) => {
    if (option.tooltip && element) {
      const rect = element.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
      });
      setHoveredOption(option);
    }
  };

  const filteredOptions = options.filter((option) =>
    itemMatchesSearchQuery(option, searchTerm, ["label"])
  );

  useEffect(() => {
    if (isOpen) {
      setKeyboardIndex(0);
    } else {
      setKeyboardIndex(null);
    }
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.id === value);

  const handleOpen = () => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
    setHoveredOption(null);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleSelect = (optionId) => {
    setSelectedOptions((prevSelected) => {
      if (prevSelected.includes(optionId)) {
        return prevSelected.filter((id) => id !== optionId);
      }
      return [...prevSelected, optionId];
    });
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange?.(null);
    setSearchTerm("");
    setIsOpen(false);
    setHoveredOption(null);
  };

  const handleKeyDown = (event) => {
    if (!isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      handleOpen();
      return;
    }

    if (!isOpen) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setKeyboardIndex((prev) => {
        const currentIndex = prev === null ? -1 : prev;
        return currentIndex < filteredOptions.length - 1 ? currentIndex + 1 : currentIndex;
      });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setKeyboardIndex((prev) => {
        const currentIndex = prev === null ? filteredOptions.length : prev;
        return currentIndex > 0 ? currentIndex - 1 : 0;
      });
    } else if (event.key === "Enter") {
      event.preventDefault();
      const optionToSelect = filteredOptions[keyboardIndex ?? 0];
      if (optionToSelect) {
        handleClose(optionToSelect.id);
        inputRef.current?.blur();
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      setSearchTerm("");
      setKeyboardIndex(null);
      setHoveredOption(null);
      if (!multiSelect) {
        setSelectedOptions([]);
      }
      inputRef.current?.blur();
    }
  };

  const getOptionBGClass = (index, optionId) => {
    if (index === keyboardIndex) {
      return "bg-gray-400 hover:bg-gray-500";
    }
    if (selectedOptions.includes(optionId)) {
      return "bg-blue-100 hover:bg-blue-200";
    }
    return "bg-gray-100 hover:bg-gray-200";
  };

  const displayText = isOpen ? searchTerm : selectedOption?.label || "";
  const dynamicPlaceholder = isOpen ? "Search..." : placeholder;

  return (
    <div className={`relative text-xs ${className}`} ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayText}
          onChange={(event) => setSearchTerm(event.target.value)}
          onFocus={handleOpen}
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
          placeholder={dynamicPlaceholder}
          disabled={disabled}
          className="m-0 h-[24px] w-full cursor-pointer rounded-md border bg-white px-[4%] py-0 pl-2 transition-colors hover:bg-gray-200 focus:bg-white focus:caret-black focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
        />
        {value && !disabled && !isOpen ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {isOpen
        ? createPortal(
            <div
              ref={listRef}
              className="fixed z-[9999] max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                minWidth: dropdownMinWidth ? `${dropdownMinWidth}px` : undefined,
              }}
            >
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 font-inter text-xs text-gray-500">
                  No options found
                </div>
              ) : (
                <ul>
                  {filteredOptions.map((option, index) => (
                    <li key={option.id}>
                      <div className="flex items-center justify-between gap-0.5">
                        <span
                          onClick={() => handleClose(option.id)}
                          onMouseEnter={(event) =>
                            handleOptionHover(option, event.currentTarget)
                          }
                          onMouseLeave={() => setHoveredOption(null)}
                          className={`mt-1 w-full cursor-pointer rounded-md px-3 py-[2px] font-inter text-xs ${getOptionBGClass(
                            index,
                            option.id
                          )}`}
                          ref={(element) => {
                            if (element) {
                              optionRefs.current[option.id] = element;
                            }
                          }}
                        >
                          {option.label}
                        </span>
                        {multiSelect ? (
                          <button
                            type="button"
                            onClick={() => handleSelect(option.id)}
                            className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-blue-100 font-bold transition-colors hover:bg-blue-200"
                          >
                            {selectedOptions.includes(option.id) ? (
                              <Minus className="h-3 w-3" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>,
            document.body
          )
        : null}

      {hoveredOption?.tooltip
        ? createPortal(
            <div
              className="fixed z-[10000] max-w-xs whitespace-normal rounded bg-gray-100 px-2 py-1 font-inter text-xs shadow-lg shadow-gray-700 pointer-events-none"
              style={{
                top: `${tooltipPosition.top - 8}px`,
                left: `${tooltipPosition.left + tooltipPosition.width / 2}px`,
                transform: "translate(-50%, -100%)",
              }}
            >
              {hoveredOption.tooltip}
              <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 transform border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100" />
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SearchableDetailsDropdown;
